<template>
	<div class="animated fadeIn">
		<div class="table-responsive">
			<table class="table table-hover table-striped">
				<thead>
					<tr>
						<th class="border-top-0">
							{{ translate('product') }}
						</th>
						<th
							class="border-top-0">
							{{ translate('sku') }}
						</th>
						<th
							class="border-top-0 text-center">
							{{ translate('quantity_sold') }}
						</th>
						<th
							class="border-top-0 text-center">
							{{ translate('quantity_returned') }}
						</th>
						<th
							class="border-top-0 text-center">
							{{ translate('quantity_total') }}
						</th>
						<th
							class="border-top-0 text-right">
							{{ translate('subtotal') }}
						</th>
						<th
							class="border-top-0 text-right">
							{{ translate('tax') }}
						</th>
						<th
							class="border-top-0 text-right">
							{{ translate('expenses') }}
						</th>
						<th
							class="border-top-0 text-right">
							{{ translate('total') }}
						</th>
					</tr>
				</thead>
				<tbody v-if="!loading && hasData">
					<template v-for="(group, index) in groupedData">
						<tr
							:key="`group-${index}`"
							class="pointer"
							@click="group.single ? null : toggleGroup(index)">
							<td class="align-middle">
								<em
									v-if="!group.single"
									:class="`fas fa-chevron-${expandedGroups[index] ? 'up' : 'down'} ml-2 mr-2`" />
								<span
									v-else
									style="display:inline-block; width:18px;"
									class="mr-2" />
								<img
									v-if="group.children[0].attributes.image"
									:src="group.children[0].attributes.image"
									alt="product_image"
									class="responsive block-inline mr-2"
									style="max-width:32px">
								<div
									v-else
									style="width:32px; display:inline-block;" />
								{{ translate(group.children[0].attributes.code_name) }} {{ group.children[0].attributes.is_bundle ? `(${translate('bundle')})` : (group.children[0].attributes.is_free ? `(${translate('free')})` : '') }}
							</td>
							<td class="align-middle">
								{{ group.single ? group.children[0].attributes.group : '—' }}
							</td>
							<td class="align-middle text-center">
								{{ group.single ? group.children[0].attributes.qty : group.summary.qty }}
							</td>
							<td class="align-middle text-center">
								{{ group.single ? group.children[0].attributes.qty_returned : group.summary.qty_returned }}
							</td>
							<td class="align-middle text-center">
								{{ group.single ? group.children[0].attributes.qty_total : group.summary.qty_total }}
							</td>
							<td class="align-middle text-right">
								{{ group.single ? group.children[0].attributes.subtotal : group.summary.subtotal }}
							</td>
							<td class="align-middle text-right">
								{{ group.single ? group.children[0].attributes.tax : group.summary.tax }}
							</td>
							<td class="align-middle text-right">
								{{ group.single ? group.children[0].attributes.expenses : group.summary.expenses }}
							</td>
							<td class="align-middle text-right">
								{{ group.single ? group.children[0].attributes.total : group.summary.total }}
							</td>
						</tr>

						<template v-if="!group.single && expandedGroups[index]">
							<tr
								v-for="(child, childIndex) in group.children"
								:key="`child-${index}-${childIndex}`">
								<td class="align-middle">
									<span
										style="display:inline-block; width:18px;"
										class="mr-2" />
									<span
										style="display:inline-block; width:32px;"
										class="mr-2" />
									{{ translate(child.attributes.code_name) }}
									{{ child.attributes.is_bundle ? `(${translate('bundle')})` : (child.attributes.is_free ? `(${translate('free')})` : '') }}
								</td>
								<td class="align-middle">
									{{ child.attributes.group }}
								</td>
								<td class="align-middle text-center">
									{{ child.attributes.qty }}
								</td>
								<td class="align-middle text-center">
									{{ child.attributes.qty_returned }}
								</td>
								<td class="align-middle text-center">
									{{ child.attributes.qty_total }}
								</td>
								<td class="align-middle text-right">
									{{ child.attributes.subtotal }}
								</td>
								<td class="align-middle text-right">
									{{ child.attributes.tax }}
								</td>
								<td class="align-middle text-right">
									{{ child.attributes.expenses }}
								</td>
								<td class="align-middle text-right">
									{{ child.attributes.total }}
								</td>
							</tr>
						</template>
					</template>

					<tr class="text-right">
						<td colspan="2">
							<b />
						</td>
						<td class="text-center">
							<b>{{ totals.qty }}</b>
						</td>
						<td class="text-center">
							<b>{{ totals.qty_returned }}</b>
						</td>
						<td class="text-center">
							<b>{{ totals.qty_total }}</b>
						</td>
						<td class="text-right">
							<b>{{ totals.subtotal }}</b>
						</td>
						<td class="text-right">
							<b>{{ totals.tax }}</b>
						</td>
						<td class="text-right">
							<b>{{ totals.expenses }}</b>
						</td>
						<td class="text-right">
							<b>{{ totals.total }}</b>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		<is-loading
			:loading-label="translate('loading')"
			:no-data-label="translate('data_not_found')"
			:loading="loading"
			:has-data="hasData" />
	</div>
</template>
<script>
import {
	MDY_FORMAT as mdyFormat,
} from '@/settings/Dates';
import {
	Report, Grids, PaymentMethods, Countries, Products,
} from '@/translations';
import FiltersParams from '@/mixins/FiltersParams';
import Sales from '@/util/Report';

export default {
	name: 'SalesProducts',
	messages: [Report, Grids, PaymentMethods, Countries, Products],
	mixins: [FiltersParams],
	data() {
		return {
			sales: new Sales(),
			expandedGroups: {},
		};
	},
	computed: {
		loading() {
			return !!this.sales.data.loading;
		},
		errors() {
			return this.sales.data.errors;
		},
		data() {
			try {
				const { data } = this.sales.data.response.data;
				return data;
			} catch (error) {
				return [];
			}
		},
		hasData() {
			const response = this.data.length;
			return !!response;
		},
		totals() {
			try {
				const { totals } = this.sales.data.response.data.meta;
				return totals;
			} catch (error) {
				return {};
			}
		},
		groupedData() {
			// 1. Bucket rows by code_name
			const groups = {};
			this.data.forEach((item) => {
				const name = item.attributes.code_name;
				if (!groups[name]) {
					groups[name] = {
						children: [],
						summary: item.attributes.summary || null,
					};
				}
				groups[name].children.push(item);
			});

			// Fields the backend does NOT aggregate for us but are plain numbers
			const EXTRA_FIELDS = ['qty_returned', 'qty_total'];

			return Object.entries(groups).map(([codeName, group]) => {
				const single = group.children.length === 1;

				if (!single) {
					// Start from the backend-supplied summary
					const summary = group.summary ? { ...group.summary } : {};

					// Add the missing numeric totals
					EXTRA_FIELDS.forEach((field) => {
						summary[field] = group.children.reduce(
							(sum, { attributes }) => sum + (attributes[field] || 0),
							0,
						);
					});

					return {
						codeName, single: false, summary, children: group.children,
					};
				}

				return {
					codeName, single: true, children: group.children,
				};
			});
		},
	},
	mounted() {
		this.$parent.$parent.rangeStart = '';
		this.$parent.$parent.rangeEnd = '';
		this.sales.getSalesByProduct().then(() => {
			const { start, end } = this.sales.data.response.data.meta.period_dates;
			this.$parent.$parent.rangeStart = this.$moment(start.date).format(mdyFormat);
			this.$parent.$parent.rangeEnd = this.$moment(end.date).format(mdyFormat);
		});
	},
	methods: {
		getDataFiltered(page) {
			if (typeof page === 'object') {
				this.filters = { ...this.filters, ...page };
			}
			const { query } = this.$route;
			const options = { ...query, ...this.filters };
			this.sales.getSalesByProduct(options);
		},
		toggleGroup(index) {
			this.$set(this.expandedGroups, index, !this.expandedGroups[index]);
		},
	},
};
</script>
